import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"
import consumer from "../../channels/consumer"

// Connects to data-controller="chat"
export default class extends Controller {
  static targets = [
    "loader",
    "messages",
    "message",
    'doubleTickIconHtml',
    'singleTickIconHtml',
    'tickIcon',
    'indicator'
  ]
  static values = {
    baseUrl: String,
    conversationId: Number,
    userId: Number
  }

  connect() {
    this.messagesElement = this.messagesTarget
    // this.loaderObserver = new IntersectionObserver(this.loadMoreMessagesOnIntersecting.bind(this), { threshold: 1.0 })
    // this.loaderObserver.observe(this.loaderTarget)
    this.element.addEventListener("scroll", this.scrollEvent.bind(this))
    this.scrollToBottom();
    this.loading = false;
    this.checkForUnreadSidebarMessages();

    this.element.addEventListener("conversation:show-messages-as-read", this.markMessagesAsRead.bind(this))
    this.element.addEventListener("message:show-as-flagged", this.showAsFlaggedMessage.bind(this))
    this.element.addEventListener("conversation:new-message", this.scrollToBottom.bind(this))
    this.element.addEventListener("conversation:deleted-message", this.handleDeletedMessage.bind(this))

    this.channel = consumer.subscriptions.create(
      { channel: "ChatChannel", conversation_id: this.conversationIdValue },
      { received: data => this.handleTyping(data) }
    )
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.scrollEvent.bind(this))
    this.element.removeEventListener("conversation:show-messages-as-read", this.markMessagesAsRead.bind(this))
    this.element.removeEventListener("message:show-as-flagged", this.showAsFlaggedMessage.bind(this))
    this.element.removeEventListener("conversation:new-message", this.scrollToBottom.bind(this))
    this.element.removeEventListener("conversation:deleted-message", this.handleDeletedMessage.bind(this))
  }

  messageTargetConnected(element) {
    // broaadcasted is when we receive a message from another user in a conversation, in realtime
    if (element.dataset.messageType === "broadcasted") {
      // this will scroll to the bottom of the conversation
      this.scrollToBottom();
      this.markAsRead()
    }
  }

  handleTyping(data) {
    // ignore our own events or non-typing actions
    if (data.action !== "typing" || data.user.id === this.userIdValue) return

    if (data.typing) {
      this.indicatorTarget.querySelector("span").textContent = `${data.user.display_name} is typing`
      this.indicatorTarget.hidden = false
    } else {
      this.indicatorTarget.hidden = true
    }
  }

  tickIconTargetConnected(element) {
    this.#showTickIcon(element)
  }

  scrollEvent(event) {
    if (event.target.scrollTop < 300 && !this.loading && !this.loaderTarget.classList.contains("hidden")) {
      this.loadMoreMessagesOnScroll();
    }
    this.checkForUnreadMessages()
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight;
  }

  // loadMoreMessagesOnIntersecting(entries) {
  //   if (!entries[0].isIntersecting) return
  //   this.loading = true;
  //   const lastMessage = this.messagesElement.querySelector(".chat:first-child")
  //   if (!lastMessage) return
  //   const lastMessageId = lastMessage.dataset.id
  //   this.fetchMessages(lastMessageId)
  // }

  loadMoreMessagesOnScroll() {
    const lastMessage = this.messagesElement.querySelector(".chat:first-child")
    if (!lastMessage) return
    const lastMessageId = lastMessage.dataset.id
    this.fetchMessages(lastMessageId)
  }

  async fetchMessages(lastMessageId) {
    if (this.loading) return
    this.loaderTarget.classList.remove("invisible")
    this.loading = true;
    // console.log("fetching messages")
    get(`${this.baseUrlValue}?last_message_id=${lastMessageId}`, { responseKind: "turbo-stream" }).then(response => {
      this.loading = false;
      // console.log("messages fetched")
      this.loaderTarget.classList.add("invisible")
      // Preserve the current scroll position to prevent automatic scrolling
      const currentScrollPosition = this.element.scrollTop
      // Ensure we're not at the very top to prevent triggering another load
      if (currentScrollPosition < 100) {
        this.element.scrollTop = 300
      } else {
        // Maintain the user's current scroll position
        this.element.scrollTop = currentScrollPosition
      }
    })
  }

  #showTickIcon(element) {
    if (element.dataset.tickIcon === "both_read") {
      this.#showDoubleTickIcon(element)
    } else if (element.dataset.tickIcon === "single_read") {
      this.#showSingleTickIcon(element)
    }
  }

  #showSingleTickIcon(element) {
    if (!element) return

    element.innerHTML = this.singleTickIconHtmlTarget.innerHTML;
  }

  #showDoubleTickIcon(element) {
    if (!element) return

    element.innerHTML = this.doubleTickIconHtmlTarget.innerHTML;
  }

  markMessagesAsRead(event) {
    const lastMessageId = event.detail.last_message_id
    const conversationId = event.detail.conversation_id

    if (conversationId !== this.conversationIdValue) return
    if (!lastMessageId) return

    this.messageTargets.forEach(message => {
      if (Number(message.dataset.id) <= lastMessageId) {
        this.#showDoubleTickIcon(message.querySelector("[data-chat-target='tickIcon']"))
      }
    })
  }

  showAsFlaggedMessage(event) {
    const messageId = event.detail.message_id
    const message = this.messageTargets.find(message => Number(message.dataset.id) === messageId)
    if (!message) return

    const chatBubble = message.querySelector(".my-message")
    if (!chatBubble) return

    chatBubble.innerHTML = "This message has been flagged as inappropriate and will be removed shortly."
    chatBubble.classList.add("bg-red-500", "text-white", "text-xs", "p-1", "px-2")

    setTimeout(() => {
      message.remove()
    }, 3000)
  }

  handleDeletedMessage(event) {
    const messageId = event.detail.message_id
    const newLastMessageBody = event.detail.new_last_message_body
    const messageBodyElement = document.getElementById(`conversation-message-body-${messageId}`)
    if (!messageBodyElement) return

    // set in the sidebar body of the last message
    messageBodyElement.innerText = newLastMessageBody
  }

  checkForUnreadMessages() {
    // check in the future if we need to mark as read using scroll event, maybe not needed
    // maybe it will be enough to use messageTargetConnected callback and just mark as read on connect()
    if (Math.round(this.element.scrollTop + this.element.clientHeight) >= Math.round(this.element.scrollHeight)) {
      this.markAsRead()
    }
  }

  markAsRead() {
    if (this.markingAsRead) return;
    if (this.isOwnLastMessage()) return;
    if (this.isLastMessageRead()) return;

    this.markingAsRead = true;

    patch(`${this.baseUrlValue}/mark_as_read`).then(() => {
      this.markLastMessageAsRead()
      this.markingAsRead = false;
      let unreadCount = document.getElementById(`unread-count-${this.conversationIdValue}`)
      if (unreadCount) {
        unreadCount.remove()
      }

      // remove "unread" from the sidebar, for current conversation if it's open
      const sidebarConversation = document.querySelector(`#sidebar-conversation-${this.conversationIdValue}`)
      if (sidebarConversation) {
        sidebarConversation.classList.add("highlighted-conversation")
        const unreadSidebarBody = sidebarConversation.querySelector(".unread-sidebar-body")
        const unreadSidebarTime = sidebarConversation.querySelector(".unread-sidebar-time")
        if (unreadSidebarBody) {
          unreadSidebarBody.classList.remove("unread-sidebar-body")
        }
        if (unreadSidebarTime) {
          unreadSidebarTime.classList.remove("unread-sidebar-time")
        }
      }
    })
  }

  lastMessage() {
    return this.messageTargets[this.messageTargets.length - 1]
  }

  isOwnLastMessage() {
    const lastMessage = this.lastMessage()
    if (!lastMessage) return false

    return lastMessage.classList.contains("chat-end")
  }

  isLastMessageRead() {
    const lastMessage = this.lastMessage()
    if (!lastMessage) return false

    return lastMessage.dataset["read"] === "true"
  }

  markLastMessageAsRead() {
    const lastMessage = this.lastMessage()
    if (!lastMessage) return

    lastMessage.dataset["read"] = "true"
  }

  checkForUnreadSidebarMessages() {
    const sidebar = document.getElementById(`sidebar-conversation-${this.conversationIdValue}`)
    if (sidebar) {
      const sidebarBody = sidebar.querySelector(`[data-conversation-show-target="sidebarBody"]`)
      const sidebarTime = sidebar.querySelector(`[data-conversation-show-target="sidebarTime"]`)
      if (sidebarBody) {
        sidebarBody.classList.add("read-sidebar-body")
        sidebarBody.classList.remove("unread-sidebar-body")
      }
      if (sidebarTime) {
        sidebarTime.classList.add("read-sidebar-time")
        sidebarTime.classList.remove("unread-sidebar-time")
      }
    }
  }
}
